/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import useStyles from 'isomorphic-style-loader/useStyles';
import loadable from '@loadable/component';
import {
    bool, func, instanceOf, object, string,
} from 'prop-types';
import { useDispatch } from 'react-redux';
import style from './EdsGuideNavModalView.module.css';
import StyledButton from '../../../../../../EnterpriseDesignSystem/Button/StyledButton';
import AddressOrZipInput from '../../../../../../EnterpriseDesignSystem/Inputs/AddressOrZipInput/AddressOrZipInput';
import Dialog from './Dialog/Dialog';
import QasSuggestionList from '../QasSuggestionList';
import LinkOrATag from '../../../../../GraphqlCommonComponents/LinkOrATag/LinkOrATag';
import { trackEvent } from '../../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { setGuidedNavModalHasBeenOpenedInSession } from '../../../../../../../../state/ducks/App/App-Actions';

const EdsSelect = loadable(() => import(/* webpackChunkName: "EdsSelect" */ '../../../../../../EnterpriseDesignSystem/Select/Select'));

const EdsGuideNavModalView = ({
    currentAddress,
    handleButtonSubmit,
    guidedNavFormData,
    showQasModel,
    handleLocationFormChange,
    handleAddressChange,
    addressFieldOnFocus,
    addressFieldOnBlur,
    handleKeyDown,
    qasSuggestions,
    onSubmitCheck,
    isOpen,
    closeModal,
    getAddressInformation,
    hideQasModel,
}) => {
    useStyles(style);
    const locations = ['Residence', 'Business', 'Funeral home', 'Hospital', 'Apartment', 'School', 'Church'];

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(setGuidedNavModalHasBeenOpenedInSession(true));
    }, []);

    const styles = {
        backgroundColor: guidedNavFormData?.background_color_modal,
        formColor: guidedNavFormData?.form_color_modal,
        addressFieldColor: guidedNavFormData?.address_field_color_modal,
        locationTypeColor: guidedNavFormData?.location_type_color_modal,
        submitButtonColor: guidedNavFormData?.submit_button_color_modal,
        submitButtonCopyColor: guidedNavFormData?.submit_button_copy_color_modal,
        bottomLinkColor: guidedNavFormData?.bottom_link_color_modal,
    };
    const handleFocus = () => (
        addressFieldOnFocus('modal')
    );

    const handleOnClick = () => {
        dispatch(trackEvent({
            eventCategory: 'Guided Navigation EDS',
            eventAction: 'Modal_ClickSubmitted',
            eventLabel: 'GNAV_Modal',
            feature_detail: 'GNAV_Modal',
            feature_element: 'Modal_ClickSubmitted',
            feature_category: 'Guided Navigation EDS',
        }));
        if (typeof document !== 'undefined') {
            document.body.style.overflow = '';
        }
        handleButtonSubmit(true);
    };
    const renderDialog = () => (
        <div className={style.guidedNavEds}>
            <h4 className={style.guidedNavEdsHeader} style={{ '--comp-form-color': styles?.formColor }}>{guidedNavFormData?.form_label_modal || 'Find Available Products'}</h4>
            <div className={style.guidedNavEdsInputWidthContainer}>
                <AddressOrZipInput
                    id="data-test-guidedNavZipOrAddressField"
                    // dataTest="hp-zipcode-native-label-placeholder"
                    label="Address or ZIP Code"
                    name="guided-nav"
                    type="text"
                    handleChange={handleAddressChange}
                    handleFocus={handleFocus}
                    handleBlur={addressFieldOnBlur}
                    handleKeyDown={handleKeyDown}
                    value={currentAddress}
                    onSubmitCheck={onSubmitCheck}
                    isOpen={isOpen}
                />
                {onSubmitCheck
                    && (
                        <div className={style.guidedNavEdsError}>
                            Enter a valid address or ZIP Code
                        </div>
                    )}
                <div id="qas-zindex-wrapper" style={{ position: 'relative', zIndex: '1000' }}>
                    {showQasModel && qasSuggestions && currentAddress?.length > 0 && currentAddress && <QasSuggestionList handleClickAway={hideQasModel} showModel={showQasModel} handlePickAnAddress={getAddressInformation} suggestions={qasSuggestions} />}
                </div>
            </div>
            <div className={style.guidedNavEdsSelectAndButtonContainer}>
                <div className={style.guidedNavEdsSelectWidthContainer}>
                    <EdsSelect
                        label="Location"
                        id="data-test-guidedNavLocation"
                        dataTestId="gnav-uncollapsed-location-dropdown"
                        name="occasion"
                        dropDownList={locations}
                        handleChange={handleLocationFormChange}
                    />
                </div>
                <div className={style.guidedNavEdsButtonWidthContainer}>
                    <StyledButton
                        StyledButton
                        label={guidedNavFormData?.submit_button_label_modal || 'Find A Gift Now'}
                        variant="light"
                        size="lg"
                        type="button"
                        fullWidth
                        attributes={{
                            style: {
                                border: `1px solid ${styles?.submitButtonColor }`,
                                backgroundColor: styles?.submitButtonColor,
                                color: styles?.submitButtonCopyColor,
                                height: '56px',
                            },
                            id: 'data-test-eds-modal-guidedNavSubmitButton',
                        }}
                        handleOnClick={() => handleOnClick()}
                    />
                </div>
            </div>
            <div className={style.guidedNavEdsBottomContainer} style={{ ' --comp-bottom-link-color': styles?.bottomLinkColor }}>
                <LinkOrATag href={guidedNavFormData?.bottom_link_href_modal || '/international-flower-delivery'} className={style.linkWrapper}>
                    <p>
                        {guidedNavFormData?.bottom_link_label_modal || 'Sending outside the US or Canada?'}
                    </p>
                </LinkOrATag>
            </div>
        </div>
    );
    return (
        createPortal(
            <Dialog
                isOpen={isOpen}
                onClose={closeModal}
                styles={styles}
            >
                {renderDialog()}
            </Dialog>,
            document.body,
        ));
};

EdsGuideNavModalView.propTypes = {
    guidedNavFormData: object.isRequired,
    currentAddress: string.isRequired,
    isOpen: bool.isRequired,
    handleAddressChange: func.isRequired,
    addressFieldOnBlur: string.isRequired,
    handleLocationFormChange: func.isRequired,
    getAddressInformation: func.isRequired,
    addressFieldOnFocus: func.isRequired,
    handleButtonSubmit: func.isRequired,
    onSubmitCheck: bool.isRequired,
    closeModal: func.isRequired,
    qasSuggestions: object,
    showQasModel: bool,
    hideQasModel: func,
    handleKeyDown: func.isRequired,
};

EdsGuideNavModalView.defaultProps = {
    qasSuggestions: {},
    showQasModel: false,
    hideQasModel: () => { },
};

export default EdsGuideNavModalView;
